import { useEffect, useState, useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// material-ui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';

// project imports
import navigation from 'menu-items';
import AdminAuthContext from 'contexts/AdminAuthContext';
import { SelectedMenuContext } from 'contexts/SelectedMenuContext';
import { filterMenuByPermissions, createAllowedModulesSet, hasFullAdminAccess } from 'utils/filterMenuByPermissions';

// types
import { NavItemType } from 'types';

// ==============================|| MENU TITLE SECTION - ALL MENUS ||============================== //

export default function MenuTitleSection() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const adminAuthContext = useContext(AdminAuthContext);
  const selectedMenuContext = useContext(SelectedMenuContext);
  const [activeMenuId, setActiveMenuId] = useState<string>('');

  // Get filtered menu items based on permissions
  const filteredMenuItems = useMemo(() => {
    let finalMenuItems = [...navigation.items];

    try {
      if (adminAuthContext) {
        const permissionsResponse = adminAuthContext?.permissions as any;
        const effectivePermissions =
          permissionsResponse?.data?.effectivePermissions || permissionsResponse?.effectivePermissions;

        if (effectivePermissions && Array.isArray(effectivePermissions) && effectivePermissions.length > 0) {
          if (!hasFullAdminAccess(effectivePermissions)) {
            const allowedModules = createAllowedModulesSet(effectivePermissions);
            const filtered = filterMenuByPermissions(finalMenuItems, allowedModules);
            if (filtered && filtered.length > 0) {
              finalMenuItems = filtered;
            }
          }
        }
      }
    } catch (error) {
      console.error('[MenuTitleSection] Error during permission filtering:', error);
    }

    return finalMenuItems;
  }, [adminAuthContext]);

  // Find active menu based on current route
  useEffect(() => {
    let activeId = '';

    filteredMenuItems.forEach((menu: NavItemType) => {
      if (menu.type === 'group') {
        // Check direct match
        if (menu?.url && menu.url === pathname) {
          activeId = menu.id!;
          return;
        }

        // Check children for a match
        if (menu.children) {
          const findInChildren = (children: NavItemType[]): boolean => {
            for (const child of children) {
              if (child.url === pathname) {
                activeId = menu.id!;
                return true;
              }
              if (child.children && findInChildren(child.children)) {
                return true;
              }
            }
            return false;
          };

          findInChildren(menu.children);
        }
      }
    });

    setActiveMenuId(activeId);
  }, [pathname, filteredMenuItems]);

  // Get first child URL or menu URL - recursively search for first item with URL
  const getMenuUrl = (item: NavItemType): string => {
    if (item.url) return item.url;
    if (item.children && item.children.length > 0) {
      // Recursively find the first item with a URL
      const findFirstUrl = (children: NavItemType[]): string => {
        for (const child of children) {
          if (child.url) return child.url;
          if (child.children && child.children.length > 0) {
            const nestedUrl = findFirstUrl(child.children);
            if (nestedUrl !== '#') return nestedUrl;
          }
        }
        return '#';
      };
      return findFirstUrl(item.children);
    }
    return '#';
  };

  // Filter only group type items
  const menuGroups = filteredMenuItems.filter((item) => item.type === 'group');

  if (menuGroups.length === 0) return null;

  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        alignItems: 'center',
        px: 4,
        height: 64,
        overflowX: 'auto',
        overflowY: 'hidden'
      }}
    >
      <Stack direction="row" spacing={0} sx={{ height: '100%' }}>
        {menuGroups.map((menu) => {
          const isActive = activeMenuId === menu.id;
          const menuUrl = getMenuUrl(menu);

          return (
            <Button
              key={menu.id}
              onClick={() => {
                navigate(menuUrl);
                selectedMenuContext?.setSelectedMenuId(menu.id!);
              }}
              sx={{
                px: 3,
                py: 0,
                minWidth: 'max-content',
                fontSize: '0.8rem',
                fontWeight: 500,
                color: isActive ? 'primary.main' : 'text.secondary',
                textTransform:"uppercase",
                backgroundColor: 'transparent',
                borderRadius: 0,
                borderBottom: isActive ? '3px solid' : '3px solid transparent',
                borderColor: isActive ? 'primary.main' : 'transparent',
                height: '100%',
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap',
                '&:hover': {
                  color: 'primary.main',
                  bgcolor: 'action.hover'
                },
                '&:disabled': {
                  color: 'text.disabled'
                }
              }}
            >
              {menu.title}
            </Button>
          );
        })}
      </Stack>
    </Box>
  );
}
